/**
 * DealWinFlow — o que perguntar DEPOIS que um negócio é marcado como ganho.
 *
 * Ganhar um negócio não transforma o lead em cliente por conta própria. O
 * fluxo, sempre por decisão do usuário:
 *   1. "Gostaria de transformar essa oportunidade em um cliente?"
 *        Não → só fica ganho (nada mais é criado).
 *        Sim → cadastro do cliente (ClienteFormPanel, com os campos
 *              obrigatórios) para o lead principal do negócio.
 *   2. Cliente salvo → "Gostaria de criar um projeto para este cliente?"
 *        Não → fim.
 *        Sim → configuração do novo projeto (WinProjectModal).
 *
 * O negócio já foi movido para ganho por quem abre este fluxo; aqui só se
 * decide o que mais criar.
 */

import { useState } from 'react'
import Icon from '../../components/icons.jsx'
import { getDealContacts, linkDealContact } from '../../db/api.js'
import ClienteFormPanel from './ClienteFormPanel.jsx'
import WinProjectModal from '../projetos/WinProjectModal.jsx'
import '../financeiro/contas.css'

function Prompt({ title, subtitle, question, hint, error, negative, positive, busy, onNegative, onPositive }) {
  return (
    <div className="fc-modal-overlay fc-modal-overlay--top" onMouseDown={onNegative}>
      <div className="fc-modal" style={{ width: 480 }} role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0 }}>
            <span style={{
              width: 34, height: 34, borderRadius: '50%', background: '#e7f9ee', border: '1px solid #bdf0d2',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon n="check" s={16} c="#17a34a" sw={2.4} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="fc-drawer-title">{title}</div>
              {subtitle && <div style={{ fontSize: 12.5, color: '#667085', marginTop: 2 }}>{subtitle}</div>}
            </div>
          </div>
          <button className="fc-drawer-x" aria-label="Fechar" onClick={onNegative}><Icon n="x" s={16} sw={2} /></button>
        </div>
        <div className="fc-modal-body">
          {error && <div className="fc-error">{error}</div>}
          <p style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{question}</p>
          {hint && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#667085' }}>{hint}</p>}
        </div>
        <div className="fc-modal-foot">
          <button className="fc-ctrl" onClick={onNegative} disabled={busy}>{negative}</button>
          <button className="fc-ctrl primary" autoFocus onClick={onPositive} disabled={busy}>{busy ? 'Aguarde…' : positive}</button>
        </div>
      </div>
    </div>
  )
}

export default function DealWinFlow({ deal, onClose, onProjectCreated, onClientConverted }) {
  const [step, setStep] = useState('ask')          // ask | form | project | newproject
  const [leadId, setLeadId] = useState(null)       // lead principal do negócio (null = cadastrar um novo)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const startClientForm = async () => {
    if (busy) return
    setBusy(true); setErr(null)
    try {
      const contacts = await getDealContacts(deal.id)
      const primary = (contacts || []).find((c) => c.is_primary) || contacts?.[0]
      setLeadId(primary?.contact_id || null)
      setStep('form')
    } catch (e) { setErr(e.message || 'Não foi possível carregar o lead do negócio.') }
    finally { setBusy(false) }
  }

  const clientSaved = async (saved) => {
    let linkError = null
    // Negócio sem lead: o cliente recém-cadastrado passa a ser o lead principal.
    if (!leadId && saved?.id) {
      try { await linkDealContact(deal.id, { contactId: saved.id, isPrimary: true }) }
      catch (e) { linkError = e.message || 'não foi possível vincular ao negócio' }
    }
    onClientConverted?.(saved)
    setErr(linkError ? `Cliente cadastrado, mas ${linkError}.` : null)
    setStep('project')
  }

  if (step === 'form') {
    return (
      <ClienteFormPanel
        contactId={leadId || undefined}
        forceStatus="cliente"
        allowDelete={false}
        elevated
        onClose={onClose}
        onSaved={clientSaved}
      />
    )
  }
  if (step === 'project') {
    return (
      <Prompt
        title="Cliente cadastrado!"
        subtitle="Os dados do cliente foram salvos."
        question="Gostaria de criar um projeto para este cliente?"
        error={err}
        negative="Não, obrigado"
        positive="Sim, criar projeto"
        onNegative={onClose}
        onPositive={() => setStep('newproject')}
      />
    )
  }
  if (step === 'newproject') {
    return <WinProjectModal deal={deal} onClose={onClose} onCreated={onProjectCreated} />
  }
  return (
    <Prompt
      title="Negócio ganho!"
      question="Gostaria de transformar essa oportunidade em um cliente?"
      hint="Se sim, você preencherá os dados obrigatórios do cliente (nome, e-mail e telefone). Se não, o negócio fica apenas marcado como ganho."
      error={err}
      negative="Não, apenas marcar como ganho"
      positive="Sim, criar cliente"
      busy={busy}
      onNegative={onClose}
      onPositive={startClientForm}
    />
  )
}
